/**
 * The model behind the signature component — the state strip ("полоса состояния").
 *
 * One column per Sentinel-2 acquisition, oldest left. Three variants carry the state
 * WITHOUT relying on colour (визуальный контракт): solid fill = accepted and the surface
 * class is clear, hatch = accepted but the class is not trustworthy, outline only =
 * rejected. Colour repeats the same information, it never carries it alone.
 *
 * Nothing here decides anything: the verdict, the event dates and the staleness all come
 * from the backend. The only judgement made in this file is *how to draw* a column, and
 * the thresholds for that live in lib/constants.ts under DISPLAY_*.
 */

import { T, days as daysWord } from "../strings";
import type { AuditRow, Run } from "../api/types";
import {
  DISPLAY_COMFORT_VALID_FRACTION,
  DISPLAY_NDVI_AXIS_MAX,
  DISPLAY_SNOW_VISIBLE_FRACTION,
  DISPLAY_UNCERTAIN_NDVI_HIGH,
  DISPLAY_UNCERTAIN_NDVI_LOW,
  PIPELINE_LOOKBACK_DAYS,
  PIPELINE_OPTICAL_STALE_DAYS,
} from "./constants";
import { formatDayMonthShort, isFiniteNumber, parseDate, percent, shiftIsoDate } from "./format";
import { collapseByDate, monthTicks, type AxisTick } from "./timeline";

export type StripVariant = "filled" | "hatched" | "outline";
export type StripTone = "vegetation" | "stubble" | "bare" | "unknown";

export interface StripColumn {
  key: string;
  acquisitionId: string;
  date: string | null;
  accepted: boolean;
  variant: StripVariant;
  tone: StripTone;
  /** 0..1 along the time axis of the window. */
  position: number;
  /** 0..1 bar height, NDVI on a fixed axis so two fields are comparable. */
  level: number;
  ndvi: number | null;
  ndmi: number | null;
  bsi: number | null;
  ndti: number | null;
  snowFraction: number | null;
  validFraction: number | null;
  badFraction: number | null;
  reasonCode: string | null;
  /** Rejection reason in words — what the hover tooltip shows. */
  reasonText: string | null;
  /** Why the accepted column is hatched, in words. */
  uncertainText: string | null;
  ariaLabel: string;
}

export interface StripEvent {
  from: string;
  to: string;
  fromPosition: number;
  toPosition: number;
  label: string;
  /** Days of uncertainty — the width of the bracket, said in words too. */
  spanDays: number;
}

export interface StripStale {
  fromPosition: number;
  days: number;
  label: string;
}

export type StripTick = AxisTick;

export interface StripModel {
  windowStart: string;
  windowEnd: string;
  columns: StripColumn[];
  /** Acquisitions the pipeline could not date (audit rows with date = null). */
  undated: StripColumn[];
  event: StripEvent | null;
  stale: StripStale | null;
  ticks: StripTick[];
  /** Acquisitions after collapsing granules of one overpass — the denominator of the rest. */
  total: number;
  acceptedCount: number;
  rejectedCount: number;
  uncertainCount: number;
  latestAcceptedDate: string | null;
}

const asPercent = (fraction: number | null | undefined): number | null =>
  isFiniteNumber(fraction) ? Math.round(fraction * 100) : null;

/** Rejection code -> a sentence with the actual numbers in it. */
export const describeRejection = (row: AuditRow): string => {
  const raw = (row.rejection_reason ?? "").trim();
  const codes = raw
    ? raw
        .split(";")
        .map((code) => code.trim())
        .filter(Boolean)
    : [];
  if (codes.length === 0) return T.rejected.reasons.UNKNOWN();

  const badPercent =
    asPercent(row.bad_fraction) ??
    (isFiniteNumber(row.bad_percent) ? Math.round(row.bad_percent) : null);
  const validPercent =
    asPercent(row.valid_fraction) ??
    (isFiniteNumber(row.valid_percent) ? Math.round(row.valid_percent) : null);

  const described = codes.map((code) => {
    switch (code) {
      case "LOCAL_BAD_PIXELS_ABOVE_LIMIT":
        return T.rejected.reasons.LOCAL_BAD_PIXELS_ABOVE_LIMIT(badPercent);
      case "VALID_COVERAGE_BELOW_LIMIT":
        return T.rejected.reasons.VALID_COVERAGE_BELOW_LIMIT(validPercent);
      case "NO_FIELD_PIXELS":
        return T.rejected.reasons.NO_FIELD_PIXELS();
      case "NO_VALID_INDICES":
        return T.rejected.reasons.NO_VALID_INDICES();
      case "LOW_VALID_COVERAGE_OR_NO_STATS":
        return T.rejected.reasons.LOW_VALID_COVERAGE_OR_NO_STATS();
      case "REJECTED_WITHOUT_REASON":
        return T.rejected.reasons.REJECTED_WITHOUT_REASON();
      default:
        // PROCESSING_ERROR arrives as "TypeName: message" (current_state.py 1373) — the
        // message is the useful part, so it is shown, not swallowed.
        return code.includes(":") ? `${T.rejected.reasons.PROCESSING_ERROR()}: ${code}` : code;
    }
  });
  return described.join(". ");
};

/**
 * NDVI of a column: the MEDIAN of the scene, with the mean only as a fallback for audits
 * stored before the median existed (strip-median). The card's zone is read off the run's
 * robust (median-family) statistic, and while the strip classified columns by `ndvi_mean`
 * one and the same acquisition stood in two zones on one screen: field 4119, 2026-07-28 —
 * the strip readout said «чистый снимок, растительность» (mean 0.372) under a card whose
 * strip line said «открытая почва» (median 0.166). One statistic per screen.
 */
const ndviOf = (row: AuditRow): number | null =>
  isFiniteNumber(row.ndvi_median)
    ? row.ndvi_median
    : isFiniteNumber(row.ndvi_mean)
      ? row.ndvi_mean
      : null;

/** Класс поверхности по NDVI. Экспортирован: полоска уровня обязана резать ось теми же
 *  границами, что и полоса состояния, иначе маркер и колонка спорят на одном экране. */
export const toneOf = (ndvi: number | null): StripTone => {
  if (!isFiniteNumber(ndvi)) return "unknown";
  if (ndvi >= DISPLAY_UNCERTAIN_NDVI_HIGH) return "vegetation";
  if (ndvi >= DISPLAY_UNCERTAIN_NDVI_LOW) return "stubble";
  return "bare";
};

/**
 * Accepted, but the MEASUREMENT should not be trusted — hatched, and we say why.
 *
 * Only the measurement. The surface class used to be a reason too ("NDVI between stubble
 * and vegetation"), and that made hatch a synonym for the stubble tone: `toneOf` returns
 * "stubble" on exactly the interval that branch fired on, so a solid stubble column was
 * unreachable and the legend advertised a state the chart could not draw. Since 4.2(б) the
 * class is read off the labelled bands of the axis — position, not pattern.
 */
const uncertaintyOf = (row: AuditRow): string | null => {
  // The same number the column is drawn and classified with (`ndviOf`): «NDVI не посчитан»
  // must not fire on a row whose median the chart is happily drawing.
  const ndvi = ndviOf(row);
  const reasons: string[] = [];
  if (ndvi === null) reasons.push(T.strip.uncertainNoNdvi);
  if (isFiniteNumber(row.valid_fraction) && row.valid_fraction < DISPLAY_COMFORT_VALID_FRACTION) {
    reasons.push(`${T.strip.valid} ${percent(row.valid_fraction)}`);
  }
  if (isFiniteNumber(row.snow_fraction) && row.snow_fraction > DISPLAY_SNOW_VISIBLE_FRACTION) {
    reasons.push(`${T.strip.snow} ${percent(row.snow_fraction)}`);
  }
  return reasons.length > 0 ? reasons.join("; ") : null;
};

const levelOf = (ndvi: number | null): number => {
  if (!isFiniteNumber(ndvi)) return 1; // unknown: full-height ghost column
  return Math.min(Math.max(ndvi, 0) / DISPLAY_NDVI_AXIS_MAX, 1);
};

const positionOf = (date: string, startMs: number, spanMs: number): number => {
  const parsed = parseDate(date);
  if (!parsed || spanMs <= 0) return 0;
  return Math.min(Math.max((parsed.getTime() - startMs) / spanMs, 0), 1);
};

const toColumn = (row: AuditRow, index: number, startMs: number, spanMs: number): StripColumn => {
  const accepted = Boolean(row.accepted);
  const ndvi = ndviOf(row);
  const uncertainText = accepted ? uncertaintyOf(row) : null;
  const variant: StripVariant = !accepted ? "outline" : uncertainText ? "hatched" : "filled";
  const tone = accepted ? toneOf(ndvi) : "unknown";
  const reasonText = accepted ? null : describeRejection(row);
  const dateLabel = row.date ? formatDayMonthShort(row.date) : T.common.noData;

  const stateWords = accepted
    ? uncertainText
      ? `${T.qa.uncertain} (${uncertainText})`
      : `${T.qa.clean}, ${
          tone === "vegetation"
            ? T.strip.bands.vegetation
            : tone === "stubble"
              ? T.strip.bands.stubble
              : T.strip.bands.bare
        }`
    : `${T.qa.rejected}: ${reasonText}`;

  return {
    key: `${row.acquisition_id || row.date || "acq"}-${index}`,
    acquisitionId: row.acquisition_id ?? "",
    date: row.date ?? null,
    accepted,
    variant,
    tone,
    position: row.date ? positionOf(row.date, startMs, spanMs) : 1,
    level: levelOf(ndvi),
    ndvi,
    ndmi: isFiniteNumber(row.ndmi_mean) ? row.ndmi_mean : null,
    bsi: isFiniteNumber(row.bsi_mean) ? row.bsi_mean : null,
    ndti: isFiniteNumber(row.ndti_mean) ? row.ndti_mean : null,
    snowFraction: isFiniteNumber(row.snow_fraction) ? row.snow_fraction : null,
    validFraction: isFiniteNumber(row.valid_fraction) ? row.valid_fraction : null,
    badFraction: isFiniteNumber(row.bad_fraction) ? row.bad_fraction : null,
    reasonCode: accepted ? null : (row.rejection_reason ?? null),
    reasonText,
    uncertainText,
    ariaLabel: T.strip.columnAria(dateLabel, stateWords),
  };
};

export const buildStripFromAudit = (
  audit: AuditRow[],
  options: {
    analysisDate: string;
    windowStart?: string | null;
    eventFrom?: string | null;
    eventTo?: string | null;
    latestOpticalDate?: string | null;
    opticalAgeDays?: number | null;
  },
): StripModel => {
  const windowEnd = options.analysisDate;
  const windowStart = options.windowStart ?? shiftIsoDate(windowEnd, -PIPELINE_LOOKBACK_DAYS);
  const startMs = parseDate(windowStart)?.getTime() ?? 0;
  const endMs = parseDate(windowEnd)?.getTime() ?? startMs + 1;
  const spanMs = Math.max(endMs - startMs, 1);

  // One column per DATE. Two granules of one overpass drew on top of each other and, worse,
  // were counted twice: `acceptedCount` was taken over the drawn columns and `rejectedCount`
  // over the raw rows, so the strip could say "18 съёмок; принята 3; неуверенно 1;
  // отклонена 16". Same rule as the calendar, now literally the same function.
  const collapsed = collapseByDate(
    audit,
    (row) => row.date ?? null,
    (row) => Boolean(row.accepted),
  );
  const sorted = [...collapsed].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  const all = sorted.map((row, index) => toColumn(row, index, startMs, spanMs));
  const columns = all.filter((column) => column.date !== null);
  const undated = all.filter((column) => column.date === null);

  const acceptedDates = columns.filter((column) => column.accepted).map((column) => column.date!);
  const latestAccepted =
    options.latestOpticalDate ?? (acceptedDates.length > 0 ? acceptedDates.at(-1)! : null);

  const event: StripEvent | null =
    options.eventFrom && options.eventTo
      ? {
          from: options.eventFrom,
          to: options.eventTo,
          fromPosition: positionOf(options.eventFrom, startMs, spanMs),
          toPosition: positionOf(options.eventTo, startMs, spanMs),
          label:
            options.eventFrom === options.eventTo
              ? formatDayMonthShort(options.eventFrom)
              : `${formatDayMonthShort(options.eventFrom)} — ${formatDayMonthShort(options.eventTo)}`,
          spanDays: Math.max(
            0,
            Math.round(
              ((parseDate(options.eventTo)?.getTime() ?? 0) -
                (parseDate(options.eventFrom)?.getTime() ?? 0)) /
                86_400_000,
            ),
          ),
        }
      : null;

  const ageDays = isFiniteNumber(options.opticalAgeDays)
    ? options.opticalAgeDays
    : latestAccepted
      ? Math.round(
          ((parseDate(windowEnd)?.getTime() ?? 0) - (parseDate(latestAccepted)?.getTime() ?? 0)) /
            86_400_000,
        )
      : null;

  const stale: StripStale | null =
    latestAccepted && isFiniteNumber(ageDays) && ageDays > PIPELINE_OPTICAL_STALE_DAYS
      ? {
          fromPosition: positionOf(latestAccepted, startMs, spanMs),
          days: ageDays,
          label: `${T.strip.legendStale}: ${daysWord(ageDays)}`,
        }
      : null;

  return {
    windowStart,
    windowEnd,
    columns,
    undated,
    event,
    stale,
    ticks: monthTicks(startMs, endMs, spanMs),
    total: all.length,
    // All three over the same set, so they add up to it. They did not: two were taken over
    // the dated columns and one over every row.
    acceptedCount: all.filter((column) => column.accepted).length,
    rejectedCount: all.filter((column) => !column.accepted).length,
    uncertainCount: all.filter((column) => column.variant === "hatched").length,
    latestAcceptedDate: latestAccepted,
  };
};

export const buildStrip = (run: Run | null | undefined): StripModel | null => {
  if (!run) return null;
  const payload = run.payload ?? null;
  const audit = payload?.optical_audit ?? [];
  if (audit.length === 0 && !payload) return null;
  return buildStripFromAudit(audit, {
    analysisDate: run.analysis_date,
    windowStart: payload?.window_start ?? payload?.optical_state?.window_start ?? null,
    eventFrom: run.result?.event_from ?? null,
    eventTo: run.result?.event_to ?? null,
    latestOpticalDate: run.result?.latest_optical_date ?? null,
    opticalAgeDays: run.result?.optical_age_days ?? null,
  });
};
